import { Box, Grid, Skeleton, styled } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { Edit } from '@styled-icons/boxicons-solid';
import unknownDocument from 'assets/img/icons/unknown-document.svg';
import defaultPartnerImage from 'assets/img/partners/default-partner-image.png';
import PaginatedMap from 'FeatureModules/Site/Components/PaginatedMap';
import Loader from 'Components/Forms/Loader';
import { usePartnerMutations } from 'FeatureModules/Partner/Components/hooks';
import PartnerDocumentOverview from 'FeatureModules/Partner/Components/PartnerDocuments/PartnerDocumentOverview';
import PartnerInviteCard from 'FeatureModules/Partner/Components/PartnerInviteCard/PartnerInviteCard';
import PartnerRequestOverview from 'FeatureModules/Partner/Components/PartnerRequests/PartnerRequestOverview';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import { OverviewHeaderContainer } from 'Components/OverviewHeader/OverviewHeader.styles';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';
import SiteTable from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import { PREVIEW_LATEST_ITEMS } from 'utils/pagination.utils';
import { EmptyStateCard, ImpactClaimsCollection, ThemeButton } from 'DesignSystem';
import LogSummary from 'DesignSystem/DataDisplay/LogSummary/LogSummary';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import ActivityItem from 'FeatureModules/Activity/Components/ActivitiesTable/ActivityItem';
import { ECompanyStatus } from 'FeatureModules/Auth/types/company.types';
import ComponentItem from 'FeatureModules/ComponentChain/Components/ComponentItem';
import usePartnerOverview from 'FeatureModules/Partner/hooks/usePartnerOverview';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import React, { FC } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { getCompanyStatusColor, getReadableCompanyStatus } from '../utils/partner.utils';

export const RequestIcon = styled('img')(() => ({
  width: 18,
}));

export const SeeAllLink = styled(Link)(({ theme }) => ({
  color: theme.custom.themeColors.primary[100],
  textDecoration: 'none',
  fontFamily: 'Lato',
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: '16px',
  padding: '4px 8px',
  '&:hover': {
    background: '#F4F5FA',
    color: '#5D6D98',
  },
}));

const PartnerOverview: FC = () => {
  const {
    partner,
    hasAnyData,
    components,
    activities,
    sites,
    requests,
    plots,
    impactClaims,
    documents,
    latestLog,
    loading,
    loadingPlots,
    error,
    isFreemium,
    isAllowedToSendRequests,
    showInvitePartner,
    showInvitePartnerNextStep,
    showReinvitePartnerNextStep,
    hasNextPlotPage,
    handleSkipInviteStep,
    handleAddActivity,
    handleEditActivity,
    handleResendInvite,
    handleEditPartner,
    handleAddComponent,
    handleEditComponent,
    handleDeleteComponent,
    handleFetchMorePlots,
  } = usePartnerOverview();
  const { handleCreateSite } = useSiteActions();
  const { handleRemoveActivity } = usePartnerMutations();
  const navigate = useNavigate();

  if (loading) return <Loader />;
  if (error || !partner) return <ErrorState action={undefined} />;

  return (
    <PageContainer>
      <PageTitle
        title="Partner overview"
        goBackLabel="All partners"
        titleExtension={partner.name}
        enableAutoGoBackHistoryButton
        goBackUrl="/partners"
      />
      <Box mb={3} />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={12} xl={8}>
          <OverviewHeader image={partner.logo} placeholderImageUrl={defaultPartnerImage}>
            <Box display="flex" justifyContent="space-between" flex={1} height="100%">
              <Box
                display="flex"
                flexDirection="column"
                flex={1}
                justifyContent="center"
                alignItems="flex-start"
              >
                <FlexBox>
                  <ThemeTypography variant="TITLE_MEDIUM" autoOverflow maxWidth={550}>
                    {partner.name}
                  </ThemeTypography>

                  {partner.externalId && (
                    <FlexBox ml={2}>
                      <ThemeTypography variant="TITLE_EXTRA_SMALL" color="GRAY_40">
                        |
                      </ThemeTypography>
                      <Box mr={1} />
                      <ThemeTypography
                        variant="TITLE_EXTRA_SMALL"
                        autoOverflow
                        maxWidth={200}
                        color="GRAY_40"
                      >
                        {partner.externalId}
                      </ThemeTypography>
                    </FlexBox>
                  )}
                </FlexBox>

                <FlexBox mb={1}>
                  <FlexBox mr={1}>
                    <Icon name="tag-filled" color="gray-80" size="small" mr={1} />
                    <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                      Supplier
                    </ThemeTypography>
                  </FlexBox>

                  {isAllowedToSendRequests && (
                    <ThemeTypography
                      variant="ITEM_SUBTITLE"
                      color={getCompanyStatusColor(partner.status)}
                    >
                      {getReadableCompanyStatus(partner.status)}
                    </ThemeTypography>
                  )}
                </FlexBox>

                <Box display="flex">
                  <ThemeButton
                    startIcon={<Edit size={14} color="white" />}
                    size="large"
                    color="PRIMARY"
                    loading={false}
                    onClick={() => handleEditPartner()}
                  >
                    Edit partner
                  </ThemeButton>

                  {showInvitePartner && (
                    <Box ml={1}>
                      <ThemeButton
                        startIcon={
                          <RequestIcon src={unknownDocument} alt="Document with question mark" />
                        }
                        size="large"
                        color="BLUE_ICE"
                        loading={false}
                        onClick={() => handleEditPartner(true)}
                      >
                        Invite partner
                      </ThemeButton>
                    </Box>
                  )}

                  {showReinvitePartnerNextStep && (
                    <Box ml={1}>
                      <ThemeButton
                        startIcon={<Icon name="send" />}
                        data-testid="resend-invite-btn"
                        size="large"
                        color="BLUE_ICE"
                        loading={false}
                        onClick={handleResendInvite}
                      >
                        Re-send invite
                      </ThemeButton>
                    </Box>
                  )}

                  {!isFreemium && (
                    <Box ml={2}>
                      <ImpactClaimsCollection
                        searchQuery={partner.name}
                        impactClaims={impactClaims}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </OverviewHeader>
        </Grid>

        {/* History of partner logs */}
        <Grid item xs={12} lg={6} xl={4}>
          <PageSubTitle title="History" mb={2} display={{ xl: 'none' }} />
          <OverviewHeaderContainer height={{ xl: '100%' }}>
            <LogSummary latestLog={latestLog} createdTimestamp={partner.createdTimestamp} />
          </OverviewHeaderContainer>
        </Grid>

        {hasAnyData && showInvitePartnerNextStep ? (
          <Grid item xs={12} lg={6} xl={12}>
            <PageSubTitle title="Next Steps" mb={2} />
            <PartnerInviteCard partner={partner} setSkipInviteStepCard={handleSkipInviteStep} />
          </Grid>
        ) : (
          <>
            {/* Latest partner sites  */}
            {!!sites.length && (
              <Grid item xs={12} lg={6}>
                <Box position="relative">
                  <PageSubTitle
                    title="Sites"
                    visibleItems={sites.length}
                    totalItems={partner.sitesCount}
                    mb={1}
                  >
                    <FlexBox gap={1}>
                      {!!partner.sitesCount && partner.sitesCount > PREVIEW_LATEST_ITEMS && (
                        <SeeAllLink to={`sites`}>See all sites</SeeAllLink>
                      )}
                      <ThemeButton
                        color="WHITE"
                        size="small"
                        startIcon={<Plus size={16} color="black" />}
                        onClick={() => handleCreateSite({ ownedBy: partner })}
                      >
                        Add new
                      </ThemeButton>
                    </FlexBox>
                  </PageSubTitle>

                  <SiteTable previewMode sites={sites} />
                </Box>
              </Grid>
            )}

            {/* Latest partner components  */}
            {!!components.length && (
              <Grid item xs={12} lg={6}>
                <Box position="relative">
                  <PageSubTitle
                    title="Components"
                    visibleItems={components.length}
                    totalItems={partner.componentsCount}
                    mb={1}
                  >
                    <FlexBox gap={1}>
                      {!!partner.componentsCount &&
                        partner.componentsCount > PREVIEW_LATEST_ITEMS && (
                          <SeeAllLink to={`components`}>See all components</SeeAllLink>
                        )}
                      <ThemeButton
                        onClick={handleAddComponent}
                        color="WHITE"
                        size="small"
                        startIcon={<Plus size={16} color="black" />}
                      >
                        Add new
                      </ThemeButton>
                    </FlexBox>
                  </PageSubTitle>

                  {components.map(component => (
                    <ComponentItem
                      key={component.id}
                      componentItem={component}
                      onEdit={handleEditComponent}
                      onDelete={handleDeleteComponent}
                      onClick={() => navigate(`/components/${component.id}`)}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Latest partner activities */}
            {!!activities.length && (
              <Grid item xs={12} lg={6}>
                <Box position="relative">
                  <PageSubTitle
                    title="Activities"
                    visibleItems={activities.length}
                    totalItems={partner.activitiesCount}
                    mb={1}
                  >
                    <FlexBox gap={1}>
                      {!!partner.activitiesCount &&
                        partner.activitiesCount > PREVIEW_LATEST_ITEMS && (
                          <SeeAllLink to={`activities`}>See all activities</SeeAllLink>
                        )}
                      <ThemeButton
                        onClick={handleAddActivity}
                        color="WHITE"
                        size="small"
                        startIcon={<Plus size={16} color="black" />}
                      >
                        Add new
                      </ThemeButton>
                    </FlexBox>
                  </PageSubTitle>
                  {activities.map(activity => (
                    <ActivityItem
                      key={activity.id}
                      activity={activity}
                      onEdit={() => handleEditActivity(activity)}
                      onDelete={() =>
                        handleRemoveActivity({ activityId: activity.id, alertFirst: true })
                      }
                      onClick={() => navigate(`/activities/${activity.id}`)}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Latest partner cultivated areas if loading */}
            {!partner?.plotsCount && loadingPlots && (
              <Grid item xs={6}>
                <Skeleton variant="rounded" width="100%" height={20} />
                <Box mb={2} />
                <Skeleton
                  variant="rounded"
                  width="100%"
                  sx={{ minHeight: 400 }}
                  height={'calc(100% - 44px)'}
                />
              </Grid>
            )}

            {/* Latest partner cultivated areas if not loading  */}
            {!!partner?.plotsCount && (
              <Grid item xs={12} lg={6}>
                <PaginatedMap
                  plots={plots}
                  loading={loadingPlots}
                  hasNextPage={hasNextPlotPage}
                  itemCount={partner.plotsCount}
                  minHeight={350}
                  onLoadMorePlots={handleFetchMorePlots}
                />
              </Grid>
            )}

            {/* Latest partner requests or   */}
            {isAllowedToSendRequests && (
              <Grid item xs={12} lg={6}>
                <PartnerRequestOverview
                  requests={requests}
                  totalRequests={partner.requestsCount}
                  partner={partner}
                />
              </Grid>
            )}

            {!!documents.length && (
              <Grid item xs={12} lg={6}>
                <PartnerDocumentOverview
                  documents={documents}
                  totalDocuments={partner.sharedDocumentsCount}
                  partner={partner}
                />
              </Grid>
            )}

            {!activities.length && (
              <Grid item xs={12} lg={6}>
                <EmptyStateCard
                  variant={EItemTypes.LOCATION}
                  onCreateItemClick={handleAddActivity}
                  buttonText="Add activity"
                  title={
                    partner.status === ECompanyStatus.INVITED ||
                    partner.status === ECompanyStatus.REGISTERED
                      ? 'Give your partner a head start and add the partner activities you know of'
                      : 'Add the activities which belong to this partner'
                  }
                  size={EAvailableSizes.LARGE}
                  background={EBackgroundTypes.WORLD}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </PageContainer>
  );
};

export default PartnerOverview;
